import { BrowserRouter, NavLink, Route, Routes } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  FileText,
  Settings as SettingsIcon,
  Target,
  UserCog,
  Users,
} from "lucide-react";
import { DashboardPage } from "./pages/Dashboard.js";
import { StaffPage } from "./pages/Staff.js";
import { GoalsPage } from "./pages/Goals.js";
import { EscalationsPage } from "./pages/Escalations.js";
import { SettingsPage } from "./pages/Settings.js";
import { StaffContextPage } from "./pages/StaffContext.js";

export function App() {
  return (
    <BrowserRouter>
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 min-h-screen">
          <Topbar />
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/goals" element={<GoalsPage />} />
            <Route path="/escalations" element={<EscalationsPage />} />
            <Route path="/settings/staff-context" element={<StaffContextPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

function Topbar() {
  const now = new Date();
  return (
    <header className="h-14 border-b border-bg-line flex items-center justify-between px-6 sticky top-0 bg-bg-base/95 backdrop-blur z-20">
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-accent-cyan to-cyan-700" />
        <span className="font-display text-[22px] font-light tracking-tight">
          Compass
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-accent-cyan border border-accent-cyan/30 rounded-sm px-1.5 py-0.5 ml-2">
          Live
        </span>
      </div>
      <div className="font-mono text-xs text-ink-dim">
        <span className="text-ink">{now.toUTCString().slice(0, 16)}</span>
        <span className="mx-2 text-ink-mute">|</span>
        <span>
          {now
            .toLocaleTimeString("en-AU", { timeZone: "Australia/Sydney", hour12: false })
            .slice(0, 5)}{" "}
          AEST
        </span>
      </div>
    </header>
  );
}

function Sidebar() {
  return (
    <aside className="w-56 border-r border-bg-line bg-bg-base sticky top-0 self-start min-h-screen py-6">
      <div className="px-6 mb-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-dimmer">
          Compass admin
        </div>
      </div>
      <nav className="px-3 space-y-0.5">
        <Nav to="/" icon={<Activity size={16} />} label="Today" end />
        <Nav to="/staff" icon={<Users size={16} />} label="Staff & org" />
        <Nav to="/goals" icon={<Target size={16} />} label="Goals" />
        <Nav
          to="/escalations"
          icon={<AlertTriangle size={16} />}
          label="Escalations"
        />
        <Nav to="/settings" icon={<SettingsIcon size={16} />} label="Settings" />
        <Nav
          to="/settings/principles"
          icon={<FileText size={16} />}
          label="Operating principles"
          indent
        />
        <Nav
          to="/settings/staff-context"
          icon={<UserCog size={16} />}
          label="Per-staff context"
          indent
        />
      </nav>
    </aside>
  );
}

function Nav({
  to,
  icon,
  label,
  end,
  indent,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  indent?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition",
          indent ? "ml-4" : "",
          isActive
            ? "bg-bg-surface text-ink"
            : "text-ink-dim hover:bg-bg-panel hover:text-ink",
        ].join(" ")
      }
    >
      <span className="opacity-80">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}
